/**
 * VAT + money module — a thin UI-layer adapter over the real, shared VAT engine
 * (`@financialharmony/tax-se`, package source: `packages/tax-se/src/se.ts`),
 * which is the SAME code the backend uses (`supabase/functions/_shared/tax/vat`
 * imports it by relative path). See that package's header for why this used to
 * be three independent copies of the category→rate table and how that drifted
 * during a real Skatteverket rate change (issue #287 follow-up).
 *
 * Backend split (see design §3.5): an LLM classifies each line's VAT *category*
 * (see `classifyLineVat`, a fast rules-based-prompt call — see
 * `LlmService.classifyVatCategory`); the shared engine maps
 * (category/description + place-of-supply facts) → final rate + treatment.
 * `decideVat` below feeds it the same facts and only adds presentation
 * concerns that are legitimately app-specific:
 *  - English reason strings (the shared engine returns Swedish ones, meant for
 *    the invoice PDF's audit trail, not this UI);
 *  - a preview-only fallback for an EU B2B buyer who hasn't entered a VAT
 *    number yet — the backend correctly BLOCKS issuing until that field is
 *    filled in (`requiredBuyerFields: ["vatNumber"]`), but the live pre-save
 *    preview must always show a concrete number, so it shows Swedish 25% until
 *    the field is filled in rather than a blocked/undefined state.
 * There is deliberately no keyword guessing here — a domestic line's rate is
 * "standard 25%, certain" until its real category has been classified. (The
 * shared engine also HAS a keyword fallback, meant for backend rows written
 * before an LLM category was ever stored — this file never triggers it, by
 * simply not passing a free-text `description` into the engine.)
 *
 * Principle: the user never picks VAT. The rule decides and we SURFACE the reason.
 */

import { resolveVatTreatment, EU_COUNTRY_CODES, type ResolveVatInput } from "@financialharmony/tax-se";
import type { Customer, CountryGroup, DocumentLanguage, InvoiceCurrency, LineItem, Totals, VatDecision } from "./types";

// English reason text per reduced/exempt category — presentation only, the rate
// itself comes from the shared engine's CATEGORY_RATE table (via
// `resolveVatTreatment`), never duplicated here. Keys mirror
// `packages/tax-se/src/se.ts`'s CATEGORY_RATE; an entry missing here (or an
// unrecognized category) just falls back to no explicit reason, same as before.
const CATEGORY_REASON: Record<string, string> = {
  reduced_food: "Food/groceries – reduced 6% VAT (temporary, through 2027)",
  reduced_hotel: "Hotel/accommodation/camping – reduced 12% VAT",
  reduced_restaurant: "Restaurant/catering (dine-in) – reduced 12% VAT",
  reduced_repair: "Repair (bicycle/shoe/leather/clothing) – reduced 12% VAT",
  reduced_art: "Artwork sale – reduced 12% VAT",
  reduced_books: "Books/newspapers – reduced 6% VAT",
  reduced_culture: "Cultural event – reduced 6% VAT",
  reduced_sports: "Sports/fitness activity – reduced 6% VAT",
  reduced_transport: "Passenger transport – reduced 6% VAT",
  reduced_skilift: "Ski-lift pass – reduced 12% VAT",
  exempt_healthcare: "Healthcare – VAT exempt",
  exempt_education: "Education – VAT exempt",
  exempt_financial: "Financial/insurance services – VAT exempt",
  exempt_museum: "Museum (publicly run/funded) – VAT exempt",
  // unsupported_margin_scheme / unsupported_property_rental deliberately have no
  // entry here: classifyVatCategory throws for these before a line's vatCategory
  // is ever set to either value (see LlmService.classifyVatCategory), so this map
  // never needs to render them — the classifyErrorLineIds error state does instead.
};

export function countryGroup(country: string): CountryGroup {
  if (country === "SE") return "SE";
  return EU_COUNTRY_CODES.has(country) ? "EU" : "non_EU";
}

// Minimal ISO-2 → English display name map for common trading partners (cosmetic
// only — the legally-relevant field is the ISO-2 country code stored on the backend).
const COUNTRY_NAMES: Record<string, string> = {
  SE: "Sweden", DE: "Germany", DK: "Denmark", NO: "Norway", FI: "Finland",
  US: "USA", GB: "United Kingdom", FR: "France", NL: "Netherlands",
  ES: "Spain", IT: "Italy", PL: "Poland", EE: "Estonia",
};
export function countryNameFor(code: string): string {
  return COUNTRY_NAMES[code.toUpperCase()] ?? code.toUpperCase();
}

// Greece uses "EL" as its VAT-number prefix even though its ISO-2 code is "GR" —
// the one common mismatch between VAT-number prefixes and ISO-3166 country codes.
const VAT_PREFIX_COUNTRY_OVERRIDES: Record<string, string> = { EL: "GR" };

/**
 * A VAT number always starts with its issuing country's 2-letter prefix (SE, DE, ...) —
 * so once the user has entered one, asking them to also pick a country separately is
 * redundant. Returns null when the value doesn't look like a VAT number yet (too
 * short, or doesn't start with 2 letters), so callers can leave country untouched.
 */
export function countryFromVatNumber(vatNumber: string): string | null {
  const match = vatNumber.trim().toUpperCase().match(/^([A-Z]{2})[A-Z0-9]{2,}$/);
  if (!match) return null;
  const prefix = match[1];
  return VAT_PREFIX_COUNTRY_OVERRIDES[prefix] ?? prefix;
}

/**
 * Builds the shared engine's input from what this UI actually has. Deliberately
 * omits `supply.description` — passing it would let the engine's keyword-fallback
 * classifier (meant for backend rows that predate any stored LLM category) guess a
 * rate here too, which this preview explicitly promises not to do (see file
 * header). `category` alone (possibly `undefined`/`null`) is enough: the engine
 * treats a missing/unrecognized category as standard 25%, "certain" — exactly
 * this UI's old behavior.
 */
function toResolveInput(customer: Customer | null, category?: string | null): ResolveVatInput {
  return {
    direction: "sale",
    seller: { countryCode: "SE", vatRegistered: true },
    buyer: customer
      ? { countryCode: customer.country, entityType: customer.type, vatNumber: customer.vatNumber ?? null }
      : { countryCode: "SE", entityType: "individual" },
    supply: { kind: "service", vatCategory: category },
  };
}

/**
 * Decide the VAT for one line given the customer and the line's LLM-classified
 * category (from `classifyLineVat`, threaded through as `LineItem.vatCategory`).
 * `category` is `undefined` before classification resolves and `null` if it
 * failed/was inconclusive — both fall back to standard 25%, same as the backend.
 *
 * The treatment/rate DECISION is the shared engine's (`resolveVatTreatment`) —
 * this function only adds English reason text and the preview-only missing-VAT-
 * number fallback described in the file header. `language` (FIN-1580, default 'en' —
 * this preview's own established default, per the file header) only affects
 * `requiredWording`, the text actually printed on the document; `reason` stays English
 * regardless — it's consultant/client-facing form UI chrome, a separate i18n concern
 * (specs/internationalization/requirements.md D12), not document content.
 */
export function decideVat(customer: Customer | null, description: string, category?: string | null, language: DocumentLanguage = "en"): VatDecision {
  const r = resolveVatTreatment(toResolveInput(customer, category), language);

  // 1) Cross-border customer — presentation text differs entirely from the
  // domestic-category branch below, even where the resulting treatment is the
  // same ("domestic", e.g. an EU private individual defaulting to Swedish VAT).
  if (customer && customer.country !== "SE") {
    if (r.treatment === "eu_reverse_charge") {
      if (r.requiredBuyerFields?.includes("vatNumber")) {
        // Preview-only fallback: the backend correctly BLOCKS issuing until the
        // VAT number is filled in — see file header.
        return {
          rate: 25,
          treatment: "domestic",
          reason: "EU company without a VAT number – Swedish 25% VAT applied",
          confidence: "certain",
        };
      }
      return {
        rate: 0,
        treatment: "eu_reverse_charge",
        requiredWording: r.requiredWording,
        reason: `EU B2B – buyer accounts for VAT (VAT ${customer.vatNumber})`,
        confidence: "certain",
      };
    }
    if (r.treatment === "export") {
      return {
        rate: 0,
        treatment: "export",
        requiredWording: r.requiredWording,
        reason: "Export outside the EU – 0% VAT",
        confidence: "certain",
      };
    }
    // EU private individual → Swedish VAT (v1 simplification; OSS is out of scope).
    return { rate: r.rate, treatment: "domestic", reason: "Private individual – Swedish 25% VAT", confidence: "certain" };
  }

  // 2) Domestic rate by the LLM-classified category — standard 25% until one is
  // known (unclassified, still classifying, or the LLM found nothing special).
  const reason = category ? CATEGORY_REASON[category] : undefined;
  if (reason) {
    return {
      rate: r.rate,
      treatment: r.treatment === "exempt" ? "exempt" : "domestic",
      reason,
      confidence: r.confidence,
    };
  }
  return { rate: r.rate, treatment: "domestic", confidence: r.confidence };
}

/**
 * Decide VAT for every line the engine still owns.
 *
 * A line the user deliberately overrode (`vatManual`) is returned untouched. This
 * function is called from every setLines()/customer-change/article-pick path, so without
 * that guard an override is wiped by the very next keystroke — it was never possible to
 * save one (FIN-603 F6/D14). The invariant belongs here, in the one function that owns
 * the rate, rather than being re-applied at each caller.
 *
 * `vatRateAutomatic` is refreshed even for an overridden line so the "Automatic VAT: X%"
 * line can keep showing what the engine would have said, next to the manual choice.
 */
export function applyVat(customer: Customer | null, lines: LineItem[], language: DocumentLanguage = "en"): LineItem[] {
  return lines.map((l) => {
    const d = decideVat(customer, l.description, l.vatCategory, language);
    const vatReason = d.reason ?? (d.rate === 25 ? "Standard VAT 25%" : undefined);
    if (l.vatManual) return { ...l, vatReason, vatRateAutomatic: d.rate };
    return { ...l, vatRate: d.rate, vatReason, vatRateAutomatic: d.rate };
  });
}

/**
 * `currency` (FIN-1580, default 'SEK' — every existing call site's unchanged behavior) only
 * changes the final rounding step: `Math.round(gross)` ("öresavrundning" to a whole krona) is a
 * SEK-specific cash-handling convention, not a generic "2-decimal" rule — found while wiring
 * this up (the plan's own design-decision table assumed computeTotals already rounded to 2
 * decimals for every currency; it actually rounds to *zero* decimals). A foreign-currency
 * invoice rounds to cents like any ordinary money figure instead.
 */
export function computeTotals(lines: LineItem[], currency: InvoiceCurrency = "SEK"): Totals {
  const net = lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);
  const map = new Map<number, { net: number; vat: number }>();
  for (const l of lines) {
    const base = l.quantity * l.unitPrice;
    if (base === 0) continue;
    const g = map.get(l.vatRate) ?? { net: 0, vat: 0 };
    g.net += base;
    g.vat += (base * l.vatRate) / 100;
    map.set(l.vatRate, g);
  }
  const vatGroups = [...map.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([rate, g]) => ({ rate, net: g.net, vat: g.vat }));
  const vatTotal = vatGroups.reduce((s, g) => s + g.vat, 0);
  const gross = net + vatTotal;
  const toPay = currency === "SEK" ? Math.round(gross) : Math.round(gross * 100) / 100;
  return { net, vatGroups, vatTotal, gross, rounding: toPay - gross, toPay };
}

const nf = (min: number, max: number) =>
  new Intl.NumberFormat("sv-SE", { minimumFractionDigits: min, maximumFractionDigits: max });

export function money(value: number): string {
  return `${nf(2, 2).format(value)} kr`;
}

export function num(value: number): string {
  return nf(0, 2).format(value);
}

/** FIN-1580: currency-aware money formatting (AC3) — SEK keeps the exact existing `money()`
 * format unchanged; the other 3 currencies use their standard symbol formatting. */
export function formatMoneyForCurrency(value: number, currency: InvoiceCurrency): string {
  if (currency === "SEK") return money(value);
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(value);
}
